import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NavParams, PopoverController } from '@ionic/angular';
import { Country } from 'src/services/country.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-country-selector-popover',
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-title>
          <b class="title">{{ 'common.select_country' | translate }}</b>
        </ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="dismiss()">
            <ion-icon name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
      <ion-toolbar class="search-bar">
        <app-search-bar 
          [value]="searchTerm"
          (search)="onSearch($event)"
          [placeholder]="searchPlaceholder"
          (keydown.enter)="$event.preventDefault()">
        </app-search-bar>
      </ion-toolbar>
    </ion-header>
    
    <ion-content class="ion-padding">
      <ion-list>
        <ion-item
          *ngFor="let country of displayedCountries"
          button
          detail="false"
          (click)="selectCountry(country)">
          <ion-avatar slot="start">
            <div class="flag-container">
              <img
                [src]="'https://flagcdn.com/' + country.code.toLowerCase() + '.svg'"
                [alt]="country.name"
                (load)="onImageLoad(country.code)"
                (error)="onImageError(country.code)"
                [class.hidden]="!isImageLoaded(country.code)">
              <ion-skeleton-text
                [animated]="true"
                class="flag-skeleton"
                *ngIf="!isImageLoaded(country.code)">
              </ion-skeleton-text>
            </div>
          </ion-avatar>
          <ion-label>
            <h2>{{ country.name }}</h2>
            <p>{{ country.dial_code }} ({{ country.code }})</p>
          </ion-label>
        </ion-item>
      </ion-list>
      
      <ion-item *ngIf="displayedCountries.length === 0">
        <ion-label class="ion-text-center" style="width: 100%;">
          {{ 'common.no_results_found' | translate }}
        </ion-label>
      </ion-item>
    </ion-content>
  `,
  styles: [`
    .title {
      font-size: 16px;
      font-weight: 800;
    }
    
    .country-flag {
      font-size: 24px;
      display: block;
      text-align: center;
    }

    ion-toolbar {
      --min-height: 40px;
    }

    .search-bar{
      --padding-start: 12px;
      --padding-end: 12px;
      --padding-top: 5px;
      --padding-bottom: 10px;
    }

    .flag-container {
      position: relative;
      width: 40px;
      height: 40px;
      border-radius: 50%;
    }

    .flag-container img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
    }

    .flag-container .flag-skeleton {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      margin: 0;
    }

    .hidden {
      display: none;
    }

    ion-item {
      --padding-start: 12px;
      --padding-end: 12px;
      --padding-top: 0;
      --margin-top: 0;
    }
    
    ion-content {
      --padding-top: 0;
      --padding-bottom: 0;
      --padding-start: 0;
      --padding-end: 0;
    }
  `]
})
export class CountrySelectorPopoverComponent implements OnInit, OnDestroy {
  @Input() countries: Country[] = [];
  @Input() selectedCountry?: Country;
  @Input() searchPlaceholder: string = 'Search...';

  displayedCountries: Country[] = [];
  searchTerm: string = '';
  private languageChangeSubscription?: Subscription;
  private loadedImages: Set<string> = new Set();

  constructor(
    private popoverController: PopoverController,
    private navParams: NavParams,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    // Initialize with all countries
    this.countries = this.navParams.get('countries') || [];
    this.selectedCountry = this.navParams.get('selectedCountry');
    this.searchPlaceholder = this.navParams.get('searchPlaceholder') || 'Search...';
    this.displayedCountries = [...this.countries];

    // Subscribe to language changes to update country names
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      // Update country names with localized versions
      this.countries = this.countries.map(country => ({
        ...country,
        name: this.translate.instant(`countries.${country.code.toLowerCase()}`) || country.name
      }));

      // Update displayed countries with localized names
      if (this.searchTerm && this.searchTerm.trim() !== '') {
        this.filterCountries(); // Re-filter with new localized names
      } else {
        this.displayedCountries = [...this.countries];
      }
    });
  }

  filterCountries() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.displayedCountries = [...this.countries];
    } else {
      const term = this.searchTerm.toLowerCase().trim();
      this.displayedCountries = this.countries.filter(country => 
        country.name.toLowerCase().includes(term) ||
        country.code.toLowerCase().includes(term) ||
        country.dial_code.includes(term)
      );
    }
  }

  selectCountry(country: Country) {
    this.popoverController.dismiss({
      selectedCountry: country
    });
  }

  onSearch(value: string){
    this.searchTerm = value;
    this.filterCountries();
  }

  dismiss() {
    this.popoverController.dismiss();
  }

  isImageLoaded(countryCode: string): boolean {
    return this.loadedImages.has(countryCode);
  }

  onImageLoad(countryCode: string): void {
    this.loadedImages.add(countryCode);
  }

  onImageError(countryCode: string): void {
    // Even if image fails to load, mark it as loaded to hide skeleton
    this.loadedImages.add(countryCode);
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription) {
      this.languageChangeSubscription.unsubscribe();
    }
  }
}